// admin/src/pages/GatewayMonitoring/components/DirectorySyncCard.js
'use strict';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import {
  getAdSyncStatus,
  startAdSync,
  listAdSyncRuns,
  getAdSyncRun,
  listAdSyncRunItems,
  extractGatewayErrorMessage,
} from '../gatewayApi';
import { FEATURES, hasFeatureAccess } from '../../../utils/rbac';

const ERROR_CODE_LABELS = {
  gateway_did_not_pick_up: 'Gateway did not pick up the request',
  lease_expired: 'Sync lease expired',
  ldap_unreachable: 'Directory server unreachable',
  ldap_auth_failed: 'Directory authentication failed',
  timeout: 'Directory request timed out',
  internal: 'Internal error occurred during directory sync',
};

const OUTCOME_CONFIG = {
  created: { label: 'Created', color: 'success' },
  updated: { label: 'Updated', color: 'info' },
  unchanged: { label: 'Unchanged', color: 'default' },
  skipped_source_conflict: { label: 'Source Conflict', color: 'warning' },
  skipped_invalid: { label: 'Invalid Payload', color: 'error' },
  disabled: { label: 'Disabled', color: 'default' },
  failed: { label: 'Failed', color: 'error' },
};

export default function DirectorySyncCard({ currentUser, mode, onRunStarted }) {
  const [statusLoading, setStatusLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Active run tracking & polling
  const [activeRun, setActiveRun] = useState(null);
  const pollIntervalRef = useRef(null);

  // Sync confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [startingSync, setStartingSync] = useState(false);

  // Run detail modal & item inspection
  const [selectedRun, setSelectedRun] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemTotal, setItemTotal] = useState(0);
  const [itemPage, setItemPage] = useState(0);
  const [itemRowsPerPage, setItemRowsPerPage] = useState(10);
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Strict permission gating: Gateway Update AND User Management Write / Create
  const canStartSync =
    hasFeatureAccess(currentUser, FEATURES.GATEWAYS, 'update') &&
    (hasFeatureAccess(currentUser, FEATURES.USER_MANAGEMENT, 'write') ||
      hasFeatureAccess(currentUser, FEATURES.USER_MANAGEMENT, 'create'));

  // Load status and recent runs
  const fetchStatusAndRuns = useCallback(async (isPolling = false) => {
    if (!isPolling) setStatusLoading(true);
    try {
      const res = await getAdSyncStatus();
      setSyncStatus(res);

      if (res?.activeRun) {
        setActiveRun(res.activeRun);
      } else {
        setActiveRun(null);
      }

      // Also refresh recent runs list
      const runsRes = await listAdSyncRuns({ page: 1, limit: 10 });
      if (runsRes && runsRes.runs) {
        setRuns(runsRes.runs);
      }
    } catch (err) {
      if (!isPolling) {
        setErrorMsg(extractGatewayErrorMessage(err));
      }
    } finally {
      if (!isPolling) setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatusAndRuns();
  }, [fetchStatusAndRuns]);

  // Polling management: Poll every 3 seconds while run is active
  useEffect(() => {
    if (activeRun && (activeRun.status === 'queued' || activeRun.status === 'running')) {
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(async () => {
          try {
            const res = await getAdSyncStatus();
            setSyncStatus(res);
            if (res?.activeRun) {
              setActiveRun(res.activeRun);
            } else {
              // Run finished!
              setActiveRun(null);
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              // Refresh historical runs
              const runsRes = await listAdSyncRuns({ page: 1, limit: 10 });
              if (runsRes && runsRes.runs) {
                setRuns(runsRes.runs);
              }
            }
          } catch (_) {}
        }, 3000);
      }
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [activeRun]);

  // Start Sync handler
  const handleStart = async (dryRun) => {
    setErrorMsg('');
    setSuccessMsg('');
    setStartingSync(true);
    try {
      const res = await startAdSync({ dryRun });
      setConfirmOpen(false);
      setSuccessMsg(
        dryRun
          ? 'Directory sync preview initiated.'
          : 'Active Directory sync initiated.'
      );
      if (res?.runId) {
        setActiveRun({
          _id: res.runId,
          status: 'queued',
          dryRun,
          queuedAt: new Date(),
        });
      }
      fetchStatusAndRuns(true);
      if (onRunStarted) onRunStarted();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || extractGatewayErrorMessage(err);
      setErrorMsg(msg);
      setConfirmOpen(false);
    } finally {
      setStartingSync(false);
    }
  };

  // Run detail modal & item fetching
  const openRunDetails = async (run) => {
    setSelectedRun(run);
    setDetailModalOpen(true);
    setItemPage(0);
    setOutcomeFilter('all');
    setSearchQuery('');
    loadRunItems(run._id, 1, itemRowsPerPage, 'all', '');
  };

  const loadRunItems = async (runId, page, limit, outcome, q) => {
    setItemsLoading(true);
    try {
      const params = { page, limit };
      if (outcome && outcome !== 'all') params.outcome = outcome;
      if (q && q.trim()) params.q = q.trim();

      const res = await listAdSyncRunItems(runId, params);
      setItems(res?.items || []);
      setItemTotal(res?.total || 0);
    } catch (err) {
      setErrorMsg('Failed to load run items: ' + extractGatewayErrorMessage(err));
    } finally {
      setItemsLoading(false);
    }
  };

  const handleOutcomeFilterChange = (val) => {
    setOutcomeFilter(val);
    setItemPage(0);
    if (selectedRun) {
      loadRunItems(selectedRun._id, 1, itemRowsPerPage, val, searchQuery);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setItemPage(0);
    if (selectedRun) {
      loadRunItems(selectedRun._id, 1, itemRowsPerPage, outcomeFilter, searchQuery);
    }
  };

  const handlePageChange = (event, newPage) => {
    setItemPage(newPage);
    if (selectedRun) {
      loadRunItems(selectedRun._id, newPage + 1, itemRowsPerPage, outcomeFilter, searchQuery);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setItemRowsPerPage(newLimit);
    setItemPage(0);
    if (selectedRun) {
      loadRunItems(selectedRun._id, 1, newLimit, outcomeFilter, searchQuery);
    }
  };

  // Helper chip styles (identical to User Management)
  const renderSourceChip = (source) => {
    const src = (source || '').toLowerCase();
    let label = 'Active Directory';
    let chipSx = {
      color: 'var(--edge-blue, #1F6FEB)',
      backgroundColor: 'var(--blue-tint, #EAF2FE)',
      border: '1px solid rgba(31, 111, 235, 0.3)',
      fontWeight: 600,
      fontSize: 11,
      height: 22,
    };
    if (src === 'scim') {
      label = 'SCIM';
      chipSx = {
        color: 'var(--secure, #00A88F)',
        backgroundColor: 'rgba(0, 168, 143, 0.12)',
        border: '1px solid rgba(0, 168, 143, 0.3)',
        fontWeight: 600,
        fontSize: 11,
        height: 22,
      };
    } else if (src === 'manual') {
      label = 'Manual';
      chipSx = {
        color: 'var(--slate, #6B7A90)',
        backgroundColor: 'var(--mist, #F7F9FC)',
        border: '1px solid var(--hairline, #E4E9F0)',
        fontWeight: 600,
        fontSize: 11,
        height: 22,
      };
    }
    return <Chip size="small" label={label} sx={chipSx} />;
  };

  const renderStatusChip = (status) => {
    const active = String(status).toLowerCase() === 'active';
    return (
      <Chip
        size="small"
        label={active ? 'Active' : 'Inactive'}
        color={active ? 'success' : 'default'}
        variant={active ? 'filled' : 'outlined'}
        sx={{ fontWeight: 600, fontSize: 11, height: 22, color: '#000000 !important' }}
      />
    );
  };

  const renderOutcomeChip = (outcome) => {
    const cfg = OUTCOME_CONFIG[outcome] || { label: outcome || 'Unknown', color: 'default' };
    return (
      <Chip
        size="small"
        label={cfg.label}
        color={cfg.color}
        sx={{ fontWeight: 600, fontSize: 11, height: 22, color: '#000000 !important' }}
      />
    );
  };

  const isEnabled = syncStatus?.enabled !== false;
  const isOnline = Boolean(syncStatus?.gatewayOnline);
  const lastRun = syncStatus?.lastRun;

  return (
    <Card
      elevation={0}
      data-testid="ad-sync-card"
      sx={{
        border: '1px solid var(--hairline, #E4E9F0)',
        borderRadius: '8px',
        mb: 3,
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <SyncIcon sx={{ color: 'var(--edge-blue, #1F6FEB)' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              Directory sync (Active Directory)
            </Typography>
          </Stack>
        }
        subheader="Synchronize user directory from on-premise Active Directory via secure outbound gateway relay."
        sx={{
          pb: 1,
          borderBottom: '1px solid var(--hairline, #E4E9F0)',
          bgcolor: 'var(--mist, #F7F9FC)',
        }}
        action={
          <Stack direction="row" spacing={1.5} sx={{ pt: 1, pr: 1 }}>
            {canStartSync && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleStart(true)}
                  disabled={startingSync || Boolean(activeRun)}
                  data-testid="ad-sync-preview-btn"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Preview changes
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => setConfirmOpen(true)}
                  disabled={startingSync || Boolean(activeRun)}
                  data-testid="ad-sync-now-btn"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: 'var(--edge-blue, #1F6FEB)',
                  }}
                >
                  Sync users now
                </Button>
              </>
            )}
          </Stack>
        }
      />

      <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
        {/* Status Line */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{
            p: 1.5,
            borderRadius: '6px',
            bgcolor: 'var(--mist, #F7F9FC)',
            border: '1px solid var(--hairline, #E4E9F0)',
            mb: 2.5,
          }}
          data-testid="ad-sync-status-line"
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--vault-navy, #0B2545)' }}>
              Status:
            </Typography>
            <Chip
              size="small"
              label={isEnabled ? 'Enabled' : 'Disabled'}
              color={isEnabled ? 'success' : 'default'}
              variant="filled"
              data-testid="ad-sync-enabled-chip"
              sx={{ fontWeight: 600, fontSize: 11, height: 22, color: '#000000 !important' }}
            />
            <Chip
              size="small"
              icon={isOnline ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
              label={isOnline ? 'Gateway Online' : 'Gateway Offline'}
              color={isOnline ? 'success' : 'error'}
              variant="outlined"
              data-testid="ad-sync-gateway-online-chip"
              sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Last Run:
            </Typography>
            {lastRun ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Chip
                  size="small"
                  label={lastRun.status}
                  color={
                    lastRun.status === 'succeeded'
                      ? 'success'
                      : lastRun.status === 'partial'
                      ? 'warning'
                      : 'error'
                  }
                  sx={{ fontWeight: 600, fontSize: 11, height: 20, color: '#000000 !important' }}
                />
                {lastRun.dryRun && (
                  <Chip
                    size="small"
                    label="Preview"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: 10, height: 18, color: '#000000 !important' }}
                  />
                )}
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {lastRun.finishedAt
                    ? new Date(lastRun.finishedAt).toLocaleString()
                    : new Date(lastRun.createdAt).toLocaleString()}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No previous runs
              </Typography>
            )}
          </Stack>
        </Stack>

        {/* Dynamic Alerts / Active Polling Banner */}
        {activeRun && (
          <Alert
            severity="info"
            icon={<CircularProgress size={18} />}
            sx={{ mb: 2.5 }}
            data-testid="ad-sync-active-banner"
          >
            {activeRun.status === 'queued'
              ? 'Waiting for the gateway to pick up the request (up to ~30 s)'
              : 'Running Active Directory synchronization...'}
          </Alert>
        )}

        {/* Offline Warning */}
        {!isOnline && (
          <Alert severity="warning" sx={{ mb: 2.5 }} data-testid="ad-sync-offline-alert">
            Gateway is offline. Queued sync runs will be processed once the gateway reconnects.
          </Alert>
        )}

        {/* Failure message banners if last run failed */}
        {lastRun?.status === 'failed' && lastRun.errorCode && (
          <Alert severity="error" sx={{ mb: 2.5 }} data-testid="ad-sync-last-error-alert">
            Last run failed: {ERROR_CODE_LABELS[lastRun.errorCode] || lastRun.errorCode}
          </Alert>
        )}

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setErrorMsg('')}>
            {errorMsg}
          </Alert>
        )}

        {successMsg && (
          <Alert severity="success" sx={{ mb: 2.5 }} onClose={() => setSuccessMsg('')}>
            {successMsg}
          </Alert>
        )}

        {/* Run History List */}
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1.5, color: 'var(--vault-navy, #0B2545)' }}
          >
            Run History
          </Typography>

          <Table size="small" data-testid="ad-sync-history-table">
            <TableHead sx={{ bgcolor: 'var(--mist, #F7F9FC)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Started</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Users Fetched</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created / Updated</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Unchanged / Skipped</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No directory sync runs recorded.
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((r) => {
                  const counts = r.counts || {};
                  return (
                    <TableRow key={r._id} hover data-testid={`ad-sync-run-row-${r._id}`}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(r.queuedAt || r.createdAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.dryRun ? 'Preview (Dry Run)' : 'Live Sync'}
                          variant="outlined"
                          color={r.dryRun ? 'info' : 'primary'}
                          sx={{ fontWeight: 600, fontSize: 10, height: 20 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.status}
                          color={
                            r.status === 'succeeded'
                              ? 'success'
                              : r.status === 'partial'
                              ? 'warning'
                              : r.status === 'failed'
                              ? 'error'
                              : 'info'
                          }
                          sx={{ fontWeight: 600, fontSize: 11, height: 22, color: '#000000 !important' }}
                        />
                      </TableCell>
                      <TableCell>{counts.fetched || 0}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          <strong style={{ color: '#2e7d32' }}>{counts.created || 0}</strong> /{' '}
                          <strong style={{ color: '#0288d1' }}>{counts.updated || 0}</strong>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {counts.unchanged || 0} /{' '}
                          {(counts.skippedSourceConflict || 0) + (counts.skippedInvalid || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => openRunDetails(r)}
                          data-testid={`view-run-details-btn-${r._id}`}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          View Results
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </CardContent>

      {/* Confirmation Dialog for Live Sync */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="sync-confirm-title"
        data-testid="sync-confirm-dialog"
      >
        <DialogTitle id="sync-confirm-title" sx={{ fontWeight: 700 }}>
          Confirm Active Directory User Synchronization
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action will query your on-premise Active Directory through the connected gateway
            and create or update user accounts in your cloud directory.
            <br />
            <br />
            Are you sure you want to proceed with live synchronization now?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleStart(false)}
            disabled={startingSync}
            data-testid="sync-confirm-submit-btn"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: 'var(--edge-blue, #1F6FEB)',
            }}
          >
            {startingSync ? <CircularProgress size={18} /> : 'Start Synchronization'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detailed Run Modal & Items Table */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="lg"
        fullWidth
        data-testid="run-detail-dialog"
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Sync Run Details
            </Typography>
            {selectedRun && (
              <>
                <Chip
                  size="small"
                  label={selectedRun.dryRun ? 'Preview (Dry Run)' : 'Live Sync'}
                  color={selectedRun.dryRun ? 'info' : 'primary'}
                  sx={{ fontWeight: 600, fontSize: 11, color: '#000000 !important' }}
                />
                <Chip
                  size="small"
                  label={selectedRun.status}
                  color={
                    selectedRun.status === 'succeeded'
                      ? 'success'
                      : selectedRun.status === 'partial'
                      ? 'warning'
                      : selectedRun.status === 'failed'
                      ? 'error'
                      : 'info'
                  }
                  sx={{ fontWeight: 600, fontSize: 11, color: '#000000 !important' }}
                />
              </>
            )}
          </Stack>
          <IconButton onClick={() => setDetailModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {selectedRun && (
            <Box sx={{ mb: 3 }}>
              {/* Summary Counts Bar */}
              <Stack
                direction="row"
                spacing={1.5}
                flexWrap="wrap"
                sx={{
                  p: 1.5,
                  borderRadius: '6px',
                  bgcolor: 'var(--mist, #F7F9FC)',
                  border: '1px solid var(--hairline, #E4E9F0)',
                  mb: 2.5,
                }}
              >
                <Chip label={`Fetched: ${selectedRun.counts?.fetched || 0}`} size="small" sx={{ color: '#000000 !important' }} />
                <Chip label={`Created: ${selectedRun.counts?.created || 0}`} color="success" size="small" sx={{ color: '#000000 !important' }} />
                <Chip label={`Updated: ${selectedRun.counts?.updated || 0}`} color="info" size="small" sx={{ color: '#000000 !important' }} />
                <Chip label={`Unchanged: ${selectedRun.counts?.unchanged || 0}`} size="small" sx={{ color: '#000000 !important' }} />
                <Chip label={`Source Conflict: ${selectedRun.counts?.skippedSourceConflict || 0}`} color="warning" size="small" sx={{ color: '#000000 !important' }} />
                <Chip label={`Invalid: ${selectedRun.counts?.skippedInvalid || 0}`} color="error" size="small" sx={{ color: '#000000 !important' }} />
                <Chip label={`Disabled: ${selectedRun.counts?.disabled || 0}`} size="small" sx={{ color: '#000000 !important' }} />
                <Chip label={`Failed: ${selectedRun.counts?.failed || 0}`} color="error" size="small" sx={{ color: '#000000 !important' }} />
              </Stack>

              {/* Filters Bar */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="outcome-filter-label">Filter by Outcome</InputLabel>
                  <Select
                    labelId="outcome-filter-label"
                    value={outcomeFilter}
                    label="Filter by Outcome"
                    onChange={(e) => handleOutcomeFilterChange(e.target.value)}
                    data-testid="outcome-filter-select"
                  >
                    <MenuItem value="all">All Outcomes</MenuItem>
                    <MenuItem value="created">Created</MenuItem>
                    <MenuItem value="updated">Updated</MenuItem>
                    <MenuItem value="unchanged">Unchanged</MenuItem>
                    <MenuItem value="skipped_source_conflict">Source Conflict</MenuItem>
                    <MenuItem value="skipped_invalid">Invalid Payload</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>

                <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: 'flex', flex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search users by name, username or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    data-testid="ad-sync-search-input"
                    InputProps={{
                      endAdornment: (
                        <IconButton type="submit" size="small">
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              </Stack>

              {/* Users Table */}
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Table size="small" data-testid="ad-sync-items-table">
                  <TableHead sx={{ bgcolor: 'var(--mist, #F7F9FC)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Employee ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>First</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Outcome</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemsLoading ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No matching records found for this sync run.
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((item) => {
                        const isLinkable = item.outcome === 'created' || item.outcome === 'updated';
                        return (
                          <TableRow key={item._id} hover data-testid={`item-row-${item._id}`}>
                            <TableCell sx={{ fontWeight: 500 }}>{item.username || '—'}</TableCell>
                            <TableCell>{item.emp_id || '—'}</TableCell>
                            <TableCell>{item.name ? item.name.split(' ')[0] : '—'}</TableCell>
                            <TableCell>{item.name ? item.name.split(' ').slice(1).join(' ') : '—'}</TableCell>
                            <TableCell>{item.email || '—'}</TableCell>
                            <TableCell>{item.mobile || '—'}</TableCell>
                            <TableCell>{item.role || '—'}</TableCell>
                            <TableCell>{item.department || '—'}</TableCell>
                            <TableCell>{renderSourceChip(item.source)}</TableCell>
                            <TableCell>{renderStatusChip(item.status)}</TableCell>
                            <TableCell>{renderOutcomeChip(item.outcome)}</TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {item.reason || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {isLinkable ? (
                                <Tooltip title="Open in User Management">
                                  <IconButton
                                    component={RouterLink}
                                    to={`/user-management?search=${encodeURIComponent(item.username)}`}
                                    size="small"
                                    color="primary"
                                    data-testid={`open-um-link-${item._id}`}
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={itemTotal}
                  page={itemPage}
                  onPageChange={handlePageChange}
                  rowsPerPage={itemRowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDetailModalOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
